import { Box, Typography } from '@mui/material';
import React from 'react';
import type { Distribution, DistributionLine } from '../../data/types';
import { dateTime, money } from '../../lib/format';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { KeyValueGrid, SectionCard } from '../../ui/primitives';

interface MethodRow {
  key: string;
  method: string;
  lines: DistributionLine[];
  after: string;
}

const total = (lines: DistributionLine[], pick: (line: DistributionLine) => number) =>
  lines.reduce((sum, line) => sum + pick(line), 0);

export default function DistributionPaymentsCard({ distribution }: { distribution: Distribution }) {
  const scope = useTenantScope();
  const paid = distribution.status === 'PAID';
  const verified = (line: DistributionLine) => scope.holderById.get(line.holderId)?.bank.verified;
  const cash = distribution.lines.filter((line) => line.method === 'CASH' && line.net > 0);
  const rows: MethodRow[] = [
    {
      key: 'direct',
      method: 'Direct credit (ABA)',
      lines: cash.filter(verified),
      after: paid ? 'Paid' : 'On approval',
    },
    {
      key: 'held',
      method: 'Held: bank details not verified',
      lines: cash.filter((line) => !verified(line)),
      after: paid ? 'Held until the investor confirms their account' : 'Held automatically',
    },
    {
      key: 'drp',
      method: 'Reinvested (DRP)',
      lines: distribution.lines.filter((line) => line.method === 'DRP'),
      after: paid ? 'Units issued' : 'Units on payment date',
    },
  ].filter((row) => row.lines.length > 0);
  const approval = scope.approvals.find(
    (item) => item.refId === distribution.id && item.status === 'APPROVED',
  );

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, mt: 2 }}>
      <SectionCard title="How it is paid" noPadding>
        <DataTable
          dense
          rows={rows}
          rowKey={(row) => row.key}
          columns={[
            { key: 'method', label: 'Method', render: (row) => row.method },
            { key: 'count', label: 'Payments', align: 'right', render: (row) => row.lines.length },
            {
              key: 'gross',
              label: 'Gross',
              align: 'right',
              render: (row) => money(total(row.lines, (line) => line.gross)),
            },
            {
              key: 'withheld',
              label: 'Tax withheld',
              align: 'right',
              render: (row) =>
                money(total(row.lines, (line) => line.tfnWithholding + line.nrWithholding)),
            },
            {
              key: 'net',
              label: 'Net',
              align: 'right',
              render: (row) => money(total(row.lines, (line) => line.net)),
            },
            { key: 'after', label: 'Status', render: (row) => row.after },
          ]}
        />
      </SectionCard>
      <SectionCard title="Approval record" subtitle="The sign-off for this payment run">
        <KeyValueGrid
          columns={1}
          items={[
            { label: 'Prepared by', value: distribution.createdBy },
            {
              label: 'Approved by',
              value: approval
                ? `${approval.decidedBy} · ${dateTime(approval.decidedAt)}`
                : (distribution.approvedBy ?? 'Waiting for approval'),
            },
            {
              label: 'Net to investors',
              value: money(
                total(distribution.lines, (line) => line.net),
                true,
              ),
            },
            { label: 'Bank file', value: distribution.abaFileName ?? 'Generated on approval' },
          ]}
        />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
          Replaces uploading a signed payment report: the second-person approval is the sign-off,
          and it is kept in the audit log with the totals it approved.
        </Typography>
      </SectionCard>
    </Box>
  );
}
